import { toValue } from "vue";
import { watchImmediate } from "@vueuse/core";
import { resolveActivatorControl } from "#ce/utils/activator-control";
import { deepActiveElement, focusWithin } from "#ce/utils/focus";

/**
 * Remembers what held focus when a menu opened and hands focus back when it closes.
 *
 * @param {object} options
 * @param {import("vue").Ref<boolean>} options.isActive - the menu's open state.
 * @param {import("vue").Ref<HTMLElement | null>} options.activator - the element wrapping the activator slot.
 * @param {import("vue").Ref<HTMLElement | null>} options.popup - the popup holding the menu content.
 * @param {import("vue").MaybeRefOrGetter<boolean>} options.skipRestore - true while the menu must leave
 *   focus where it is.
 * @returns {{
 *   activatorControl: () => HTMLElement | null | undefined,
 *   markClosedByOutsideClick: () => void,
 *   returnFocus: () => void,
 * }}
 */
export function useMenuFocusReturn({ isActive, activator, popup, skipRestore }) {
  let opener = null;
  let closedByOutsideClick = false;

  function rememberOpener(open) {
    if (open) {
      opener = deepActiveElement();
      closedByOutsideClick = false;
    }
  }

  watchImmediate(isActive, rememberOpener);

  /** Returns the focusable control the consumer slotted into the activator. */
  function activatorControl() {
    const [slotted] = activator.value?.querySelector("slot")?.assignedElements({ flatten: true }) ?? [];

    return resolveActivatorControl(slotted);
  }

  /** Records that an outside click closed the menu, so focus stays where the click put it. */
  function markClosedByOutsideClick() {
    closedByOutsideClick = true;
  }

  /**
   * Returns focus to the element that had it when the menu opened, or to the activator's control when
   * that element is `body` or cannot take focus. Does nothing unless focus sits inside the popup or on
   * `body`. Also does nothing after an outside click and while `skipRestore` is true.
   */
  function returnFocus() {
    const remembered = opener;
    opener = null;

    if (toValue(skipRestore) || closedByOutsideClick) {
      return;
    }

    const active = deepActiveElement();
    if (active && active !== document.body && !focusWithin(popup.value)) {
      return;
    }

    for (const candidate of [remembered, activatorControl()]) {
      if (candidate && candidate !== document.body) {
        candidate.focus?.({ preventScroll: true });
        if (focusWithin(candidate)) {
          return;
        }
      }
    }
  }

  return { activatorControl, markClosedByOutsideClick, returnFocus };
}
